import React, { useState } from 'react';
import { DatePicker, Select, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTaskDrawer } from '../TaskDrawerContext';
import Avatar from './Avatar';
import EffortBar from './EffortBar';
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from '../constants';
import { getFullName } from '../utils/personUtils';

const STATUS_DOT = {
    'Backlog': '#94a3b8',
    'In Progress': '#3b82f6',
    'Under Review': '#f59e0b',
    'Completed': '#10b981',
    'Closed': '#10b981',
    'Close': '#10b981',
    'On Hold': '#f97316',
    'Cancelled': '#ef4444',
};

const PRIORITY_COLOR = {
    'Low': '#22c55e',
    'Medium': '#f59e0b',
    'High': '#ef4444',
    'Critical': '#7c3aed',
    'None': '#94a3b8',
};

const DEP_LABELS = {
    finish_to_start: 'Finish → Start',
    start_to_start: 'Start → Start',
    finish_to_finish: 'Finish → Finish',
};

export default function TaskMetadata() {
    const {
        task,
        employees,
        projects,
        projectTasks,
        dependencies,
        additionalProjects,
        displayActualTimeMs,
        remainingTimeMs,
        editPriority,
        setEditPriority,
        editEstHours,
        setEditEstHours,
        editEstMinutes,
        setEditEstMinutes,
        editEffortHours,
        setEditEffortHours,
        editEffortMinutes,
        setEditEffortMinutes,
        statusUpdating,
        handleAssigneeChange,
        handleStartDateChange,
        handleDueDateChange,
        handleStatusChange,
        handleAddDependency,
        handleRemoveDependency,
        handleAddProject,
        handleRemoveAdditionalProject,
        mode,
        handlePrimaryProjectChange,
        milestoneId,
        projectMilestones,
        handleMilestoneChange,
        itemType,
        milestoneOwner,
        setMilestoneOwner,
        completionCriteria,
        setCompletionCriteria,
        deliverables,
        setDeliverables,
        initialChildTasks,
        handleAddInitialChildTask,
        handleRemoveInitialChildTask,
    } = useTaskDrawer();

    const [pendingBlocker, setPendingBlocker] = useState('');
    const [pendingType, setPendingType] = useState('finish_to_start');
    const [addingDep, setAddingDep] = useState(false);
    const [showProjectAdder, setShowProjectAdder] = useState(false);
    const [newDeliverableTitle, setNewDeliverableTitle] = useState('');
    const [newChildTaskTitle, setNewChildTaskTitle] = useState('');

    const isMilestone = (task?.itemType === 'milestone') || (itemType === 'milestone');

    const availableBlockers = (projectTasks || []).filter(
        t => t._id !== task?._id &&
            !(dependencies || []).some(d => String(d.taskId?._id || d.taskId) === String(t._id))
    );

    const primaryProjectSelected = task?.project?._id || task?.project;

    const rawMsId = milestoneId || task?.milestoneId?._id || (typeof task?.milestoneId === 'string' ? task?.milestoneId : null);
    const currentSelectedMilestoneId = rawMsId ? String(rawMsId) : null;

    const availableProjects = (projects || []).filter(
        p => p._id !== primaryProjectSelected &&
            !(additionalProjects || []).some(ap => ap._id === p._id)
    );

    const hasDeps = (dependencies || []).length > 0;
    const projectCount = (mode === 'create' ? 0 : (task?.project ? 1 : 0)) + (additionalProjects || []).length;
    const hasProjectSelected = Boolean(primaryProjectSelected);

    const handleAddDeliverable = () => {
        if (!newDeliverableTitle.trim()) return;
        const updated = [...(deliverables || []), { title: newDeliverableTitle.trim(), done: false }];
        setDeliverables(updated);
        setNewDeliverableTitle('');
    };

    const handleToggleDeliverable = (index) => {
        const updated = (deliverables || []).map((item, idx) =>
            idx === index ? { ...item, done: !item.done } : item
        );
        setDeliverables(updated);
    };

    const handleRemoveDeliverable = (index) => {
        const updated = (deliverables || []).filter((_, idx) => idx !== index);
        setDeliverables(updated);
    };

    return (
        <section className="rounded-2xl bg-[#f5f6f8] dark:bg-[#141414] border border-[#e7e8e9] dark:border-[#303030] p-5 space-y-5 text-[13px] text-[#1c1e21] dark:text-[#e2e8f0]">

            {/* ── Milestone Progress Card (View/Edit Mode) ── */}
            {isMilestone && mode !== 'create' && (
                <div className="bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent p-4 rounded-xl border border-amber-300 dark:border-amber-900/50 space-y-2.5">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2.5">
                            <span className="text-amber-500 font-black text-xl">◆</span>
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                                    Milestone Progress
                                </span>
                                <h4 className="font-extrabold text-sm text-[#1c1e21] dark:text-white">
                                    {task?.completedChildTasks ?? 0} of {task?.totalChildTasks ?? 0} Child Tasks Completed
                                </h4>
                            </div>
                        </div>
                        <span className="text-xs font-black px-2.5 py-1 rounded-full bg-amber-200 dark:bg-amber-950 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800">
                            {task?.progress ?? 0}%
                        </span>
                    </div>

                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                            style={{ width: `${task?.progress ?? 0}%` }}
                        />
                    </div>
                </div>
            )}

            {/* ── Assignee / Milestone Owner ── */}
            <div className="flex items-center gap-4">
                <span className="w-28 text-[#65676b] font-normal">
                    {isMilestone ? 'Milestone Owner' : 'Assignee'}
                </span>
                <div className="flex-1 min-w-0">
                    {(isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee) ? (
                        <div className="flex items-center gap-2 group">
                            <Avatar person={isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee} size={24} />
                            <span className="font-semibold text-[#1c1e21]">
                                {getFullName(isMilestone ? (milestoneOwner || task?.milestoneOwner || task?.assignee) : task?.assignee)}
                            </span>
                            <button
                                onClick={() => isMilestone ? setMilestoneOwner(null) : handleAssigneeChange(null)}
                                className="text-[#65676b] hover:text-[#1c1e21] flex items-center"
                            >
                                <span className="material-symbols-outlined text-base">close</span>
                            </button>
                        </div>
                    ) : (
                        <Select
                            placeholder={<span className="text-[#bec3c9]">{isMilestone ? 'Select owner' : 'No assignee'}</span>}
                            className="w-48"
                            variant="borderless"
                            showSearch
                            optionFilterProp="label"
                            onChange={(val) => {
                                const selected = employees.find(e => e._id === val);
                                if (isMilestone) {
                                    setMilestoneOwner(selected || { _id: val });
                                } else {
                                    handleAssigneeChange(val);
                                }
                            }}
                            value={null}
                            size="small"
                            style={{ paddingLeft: 0, marginLeft: '-8px' }}
                        >
                            {employees.map((employee) => (
                                <Select.Option key={employee._id} value={employee._id} label={getFullName(employee)}>
                                    <div className="flex items-center gap-2">
                                        <Avatar person={employee} size={18} />
                                        <span className="text-xs">{getFullName(employee)}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </div>
            </div>

            {/* ── Start Date ── */}
            <div className="flex items-center gap-4">
                <span className="w-28 text-[#65676b] font-normal">Start date</span>
                <div className="flex-1 min-w-0">
                    {task.startDate ? (
                        <div className="flex items-center gap-2 group">
                            <span className="material-symbols-outlined text-base text-[#65676b]">calendar_month</span>
                            <span className="font-semibold text-[#1c1e21]">
                                {dayjs(task.startDate).format('MMM DD, YYYY')}
                            </span>
                            <button
                                onClick={() => handleStartDateChange(null)}
                                className="text-[#65676b] hover:text-[#1c1e21] flex items-center"
                            >
                                <span className="material-symbols-outlined text-base">close</span>
                            </button>
                        </div>
                    ) : (
                        <DatePicker
                            variant="borderless"
                            format="MMM DD, YYYY"
                            placeholder="No start date"
                            size="small"
                            suffixIcon={<span className="material-symbols-outlined text-base text-[#65676b]">calendar_month</span>}
                            onChange={handleStartDateChange}
                            allowClear={false}
                            style={{ paddingLeft: 0, marginLeft: '-8px' }}
                        />
                    )}
                </div>
            </div>

            {/* ── Due Date ── */}
            <div className="flex items-center gap-4">
                <span className="w-28 text-[#65676b] font-normal">Due date</span>
                <div className="flex-1 min-w-0">
                    {task.dueDate ? (
                        <div className="flex items-center gap-2 group flex-wrap">
                            <div className="flex items-center gap-1.5 border border-[#dddfe2] dark:border-[#303030] bg-white dark:bg-[#262626] rounded-full px-2.5 py-1">
                                <span className="material-symbols-outlined text-base text-[#65676b] dark:text-slate-400">calendar_today</span>
                                <span className="font-semibold text-[#1c1e21] dark:text-white">
                                    {dayjs(task.dueDate).format('MMM DD, YYYY')}
                                </span>
                                <button
                                    onClick={() => handleDueDateChange(null)}
                                    className="text-[#65676b] hover:text-red-500 flex items-center ml-1"
                                >
                                    <span className="material-symbols-outlined text-sm font-bold">close</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <DatePicker
                            variant="borderless"
                            format="MMM DD, YYYY"
                            placeholder="No due date"
                            size="small"
                            suffixIcon={<span className="material-symbols-outlined text-base text-[#65676b]">calendar_today</span>}
                            onChange={handleDueDateChange}
                            allowClear={false}
                            style={{ paddingLeft: 0, marginLeft: '-8px' }}
                        />
                    )}
                </div>
            </div>

            {/* ── Status ── */}
            <div className="flex items-center gap-4">
                <span className="w-28 text-[#65676b] font-normal">Status</span>
                <div className="flex-1 min-w-0">
                    <Select
                        value={['Closed', 'Close'].includes(task?.status) ? 'Completed' : (task?.status || 'Backlog')}
                        onChange={handleStatusChange}
                        variant="borderless"
                        size="small"
                        loading={statusUpdating}
                        className="min-w-[150px]"
                        style={{ paddingLeft: 0, marginLeft: '-8px' }}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <Select.Option key={option} value={option}>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: STATUS_DOT[option] || '#94a3b8' }}
                                    />
                                    <span className="text-[13px]">{option}</span>
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            {/* ── Priority ── */}
            <div className="flex items-center gap-4">
                <span className="w-28 text-[#65676b] font-normal">Priority</span>
                <div className="flex-1 min-w-0">
                    <Select
                        value={editPriority}
                        onChange={setEditPriority}
                        variant="borderless"
                        size="small"
                        className="min-w-[130px]"
                        style={{ paddingLeft: 0, marginLeft: '-8px' }}
                    >
                        {PRIORITY_OPTIONS.map((option) => (
                            <Select.Option key={option} value={option}>
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: PRIORITY_COLOR[option] || '#94a3b8' }}
                                    />
                                    <span className="text-[13px]">{option}</span>
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>

            {/* ── Sub-Category ── */}
            {(task?.subCategory || task?.project?.subCategory) && (
                <div className="flex items-center gap-4">
                    <span className="w-28 text-[#65676b] font-normal">Sub-Category</span>
                    <div className="flex-1 min-w-0">
                        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white bg-white dark:bg-[#262626] border border-[#dddfe2] dark:border-[#303030] rounded-full px-2.5 py-0.5">
                            <span className="material-symbols-outlined text-sm text-[#65676b]">label</span>
                            {task.subCategory || task.project.subCategory}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Milestone Specific: Completion Criteria ── */}
            {isMilestone && (
                <div className="flex items-start gap-4 pt-1">
                    <div className="w-28 pt-2">
                        <span className="text-[#65676b] font-normal block">Completion Criteria</span>
                        <span className="text-[10px] text-[#94a3b8] font-normal block leading-tight">PM approval conditions</span>
                    </div>
                    <div className="flex-1 space-y-2">
                        <textarea
                            rows={6}
                            value={completionCriteria}
                            onChange={(e) => setCompletionCriteria(e.target.value)}
                            placeholder="Describe what must be true before this milestone is marked completed (e.g. Final designs completed, reviewed, all revisions addressed, and assets uploaded)."
                            className="w-full bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl p-3.5 text-sm text-[#1c1e21] dark:text-white focus:ring-2 focus:ring-[#03488a] outline-none transition-all shadow-sm min-h-[140px]"
                        />
                        {/* Quick Suggestions Pills */}
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                            <span className="text-[11px] font-bold text-slate-400 self-center mr-1">Quick Suggestions:</span>
                            {[
                                'All child tasks completed & verified',
                                'Designs reviewed & final assets uploaded',
                                'QA testing passed & staging approved',
                                'Client confirmation & approval received',
                            ].map((suggestion, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                        if (!completionCriteria.trim()) {
                                            setCompletionCriteria(suggestion);
                                        } else if (!completionCriteria.includes(suggestion)) {
                                            setCompletionCriteria(prev => `${prev}\n• ${suggestion}`);
                                        }
                                    }}
                                    className="text-[11px] font-medium bg-slate-200/70 hover:bg-[#03488a] hover:text-white dark:bg-slate-800 dark:hover:bg-[#03488a] text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-lg transition-all"
                                >
                                    💡 {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Milestone Specific: Expected Deliverables ── */}
            {isMilestone && (
                <div className="flex items-start gap-4 pt-1">
                    <span className="w-28 text-[#65676b] font-normal pt-3">Deliverables</span>
                    <div className="flex-1 space-y-2.5">
                        {(deliverables || []).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2.5 bg-white dark:bg-[#1f1f1f] p-3 rounded-xl border border-[#dddfe2] dark:border-[#303030]">
                                <input
                                    type="checkbox"
                                    checked={item.done}
                                    onChange={() => handleToggleDeliverable(idx)}
                                    className="rounded text-[#03488a] cursor-pointer w-4 h-4"
                                />
                                <span className={`flex-1 text-sm ${item.done ? 'line-through text-slate-400' : 'font-semibold text-[#1c1e21] dark:text-white'}`}>
                                    {item.title || item.name}
                                </span>
                                <button
                                    onClick={() => handleRemoveDeliverable(idx)}
                                    className="text-slate-400 hover:text-red-500 p-1"
                                >
                                    <span className="material-symbols-outlined text-base">close</span>
                                </button>
                            </div>
                        ))}

                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newDeliverableTitle}
                                onChange={(e) => setNewDeliverableTitle(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDeliverable())}
                                placeholder="Add deliverable title (e.g. Approved UI Prototype)…"
                                className="flex-1 h-12 bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl px-4 text-sm outline-none focus:border-[#03488a] shadow-sm"
                            />
                            <button
                                onClick={handleAddDeliverable}
                                disabled={!newDeliverableTitle.trim()}
                                className="h-12 px-5 bg-[#03488a] text-white text-sm font-bold rounded-xl disabled:opacity-40 hover:bg-[#003263] transition-colors shrink-0 shadow-sm flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-lg">add</span>
                                Add Deliverable
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* ── Task Date Metadata ── */}
            {mode !== 'create' && (
                <div className="space-y-3 pt-1 border-t border-[#e7e8e9] dark:border-[#303030]">

                    {/* Created on */}
                    {task?.createdAt && (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Created on</span>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="material-symbols-outlined text-sm text-[#65676b]">add_circle</span>
                                <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white">
                                    {dayjs(task.createdAt).format('MMM D, YYYY, h:mm A')}
                                </span>
                                {task.createdBy && (
                                    <div className="flex items-center gap-1.5 ml-1">
                                        <Avatar person={task.createdBy} size={16} />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400 truncate">
                                            {getFullName(task.createdBy)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Last Updated */}
                    {task?.updatedAt && (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Last updated</span>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="material-symbols-outlined text-sm text-[#65676b]">update</span>
                                <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white">
                                    {dayjs(task.updatedAt).format('MMM D, YYYY, h:mm A')}
                                </span>
                                {task.lastUpdatedBy && (
                                    <div className="flex items-center gap-1.5 ml-1">
                                        <Avatar person={task.lastUpdatedBy} size={16} />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400 truncate">
                                            {getFullName(task.lastUpdatedBy)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Completed on — only when task is Completed or completedOn exists */}
                    {(task?.status === 'Completed' || task?.completedOn) && (
                        <div className="flex items-center gap-4">
                            <span className="w-28 text-[#65676b] font-normal shrink-0">Completed on</span>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="material-symbols-outlined text-sm text-emerald-500">check_circle</span>
                                <span className="text-[13px] font-semibold text-emerald-600 dark:text-emerald-400">
                                    {dayjs(task.completedOn || task.updatedAt).format('MMM D, YYYY, h:mm A')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Dependencies ── */}
            <div className="flex items-start gap-4">
                <span className="w-28 text-[#65676b] font-normal pt-0.5">Dependencies</span>
                <div className="flex-1 space-y-1.5">
                    {(dependencies || []).map((dep) => {
                        const depTask = dep.taskId && typeof dep.taskId === 'object' ? dep.taskId : null;
                        const depId = String(dep.taskId?._id || dep.taskId);
                        const depTitle = depTask?.title || projectTasks.find(t => t._id === depId)?.title || depId;
                        const depTaskId = depTask?.taskId || projectTasks.find(t => t._id === depId)?.taskId;
                        const depStatus = depTask?.status || projectTasks.find(t => t._id === depId)?.status;
                        const depType = dep.type || 'finish_to_start';

                        return (
                            <div key={depId} className="flex items-center gap-2 bg-[#f0f2f5] border border-[#dddfe2] rounded-lg px-3 py-2 group">
                                <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: STATUS_DOT[depStatus] || '#94a3b8' }}
                                />
                                <Tooltip title={`${depTitle} (${depTaskId})`} placement="topLeft">
                                    <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white flex-1 truncate select-none cursor-help">
                                        {depTitle}
                                    </span>
                                </Tooltip>
                                {depTaskId && (
                                    <span className="text-[11px] font-mono text-[#65676b] flex-shrink-0">{depTaskId}</span>
                                )}
                                <span className="text-[11px] text-[#006591] bg-[#dff2ff] px-2 py-0.5 rounded-full font-semibold flex-shrink-0">
                                    {DEP_LABELS[depType] || depType}
                                </span>
                                <button
                                    onClick={() => handleRemoveDependency(depId)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-[#65676b] hover:text-red-500 shrink-0 ml-2 animate-fadeIn"
                                    title="Remove dependency"
                                >
                                    <span className="material-symbols-outlined text-sm">close</span>
                                </button>
                            </div>
                        );
                    })}

                    {addingDep && (
                        <div className="flex items-center gap-2 bg-white dark:bg-[#1f1f1f] p-2 rounded-lg border border-[#dddfe2] dark:border-[#303030]">
                            <Tooltip title={pendingBlocker ? (availableBlockers.find(t => t._id === pendingBlocker)?.title || '') : ''} placement="top">
                                <Select
                                    showSearch
                                    placeholder="Select task…"
                                    value={pendingBlocker || undefined}
                                    onChange={(val) => setPendingBlocker(val)}
                                    optionFilterProp="label"
                                    className="w-48 text-[12px]"
                                    size="small"
                                    popupClassName="dark:bg-[#1e293b]"
                                >
                                    {availableBlockers.map(t => (
                                        <Select.Option key={t._id} value={t._id} label={`${t.title} (${t.taskId})`}>
                                            <div className="text-xs truncate">
                                                {t.title} ({t.taskId})
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Tooltip>
                            <Select
                                value={pendingType}
                                onChange={(val) => setPendingType(val)}
                                className="w-32 text-[12px]"
                                size="small"
                                popupClassName="dark:bg-[#1e293b]"
                            >
                                <Select.Option value="finish_to_start">Finish → Start</Select.Option>
                                <Select.Option value="start_to_start">Start → Start</Select.Option>
                                <Select.Option value="finish_to_finish">Finish → Finish</Select.Option>
                            </Select>
                            <button
                                onClick={() => {
                                    if (!pendingBlocker) return;
                                    handleAddDependency(pendingBlocker, pendingType);
                                    setPendingBlocker('');
                                    setPendingType('finish_to_start');
                                    setAddingDep(false);
                                }}
                                disabled={!pendingBlocker}
                                className="text-[12px] font-bold text-white bg-[#03488a] hover:bg-[#003263] disabled:opacity-40 px-3 py-1 rounded transition-colors shrink-0"
                            >
                                Add
                            </button>
                            <button
                                onClick={() => { setPendingBlocker(''); setAddingDep(false); }}
                                className="text-[#65676b] hover:text-[#1c1e21] dark:text-slate-400 dark:hover:text-white"
                            >
                                <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                        </div>
                    )}

                    {!addingDep && (
                        <button
                            onClick={() => setAddingDep(true)}
                            className="text-[13px] text-[#65676b] hover:text-[#1c1e21] font-medium"
                        >
                            + Add dependency
                        </button>
                    )}
                </div>
            </div>

            {/* ── Projects Section ── */}
            <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                    <h4 className="text-[14px] font-bold text-[#1c1e21]">Projects</h4>
                    <span className="text-[12px] font-bold text-[#65676b] bg-white border border-[#dddfe2] w-5 h-5 rounded-full flex items-center justify-center">
                        {projectCount}
                    </span>

                    {/* Add Project inline button */}
                    {!showProjectAdder && (availableProjects.length > 0 || !primaryProjectSelected) && (
                        <button
                            onClick={() => setShowProjectAdder(true)}
                            className="text-[#65676b] hover:text-[#1c1e21] flex items-center ml-1"
                        >
                            <span className="material-symbols-outlined text-lg">add</span>
                        </button>
                    )}

                    {showProjectAdder && (
                        <Select
                            placeholder="Add project…"
                            onChange={(val) => {
                                if (!primaryProjectSelected) {
                                    handlePrimaryProjectChange(val);
                                } else {
                                    handleAddProject(val);
                                }
                                setShowProjectAdder(false);
                            }}
                            value={null}
                            variant="outlined"
                            size="small"
                            showSearch
                            optionFilterProp="label"
                            className="ml-2 w-48 text-xs"
                            popupClassName="dark:bg-[#1e293b]"
                            onBlur={() => setShowProjectAdder(false)}
                            defaultOpen
                        >
                            {/* If primary project isn't set, present all projects. Otherwise present availableProjects */}
                            {(!primaryProjectSelected ? projects : availableProjects).map((p) => (
                                <Select.Option key={p._id} value={p._id} label={p.name}>
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color || '#65676b' }} />
                                        <span className="text-xs">{p.name}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </div>

                <div className="space-y-2.5">
                    {/* If no primary project is selected, show an inviting trigger to assign project */}
                    {!primaryProjectSelected && !showProjectAdder && (
                        <div
                            onClick={() => setShowProjectAdder(true)}
                            className="flex items-center justify-between p-3 rounded-xl border border-dashed border-[#dddfe2] dark:border-[#303030] bg-white/60 dark:bg-[#1f1f1f]/60 hover:bg-white dark:hover:bg-[#1f1f1f] hover:border-[#03488a] cursor-pointer transition-colors"
                        >
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs font-medium">
                                <span className="material-symbols-outlined text-base">domain</span>
                                <span>Assign this task to a project...</span>
                            </div>
                            <span className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] hover:underline">
                                + Select Project
                            </span>
                        </div>
                    )}

                    {/* Render primary project (if any) and additional projects as equal items */}
                    {task?.project && (
                        <>
                            <ProjectRow
                                project={typeof task.project === 'object' ? task.project : (projects.find(p => String(p._id) === String(task.project)) || { _id: task.project, name: 'Selected Project' })}
                                onRemove={() => {
                                    // If they remove the primary project, clear it
                                    handlePrimaryProjectChange(null);
                                }}
                                editEstHours={editEstHours}
                                setEditEstHours={setEditEstHours}
                                editEstMinutes={editEstMinutes}
                                setEditEstMinutes={setEditEstMinutes}
                                editEffortHours={editEffortHours}
                                setEditEffortHours={setEditEffortHours}
                                editEffortMinutes={editEffortMinutes}
                                setEditEffortMinutes={setEditEffortMinutes}
                            />

                            {/* Milestone selector dropdown for Primary Project */}
                            {!isMilestone && (
                                <div className="flex items-center gap-2 bg-white dark:bg-[#1f1f1f] border border-[#dddfe2] dark:border-[#303030] rounded-xl px-3.5 py-2 shadow-sm">
                                    <span className="text-amber-500 font-bold text-base shrink-0">◆</span>
                                    <span className="text-[12px] font-semibold text-[#65676b] dark:text-slate-400 shrink-0">
                                        Milestone
                                    </span>
                                    <Select
                                        placeholder={
                                            <span className="text-[#bec3c9]">
                                                {(projectMilestones || []).length > 0
                                                    ? `Assign to Milestone (${projectMilestones.length} available)...`
                                                    : 'No milestones in this project'}
                                            </span>
                                        }
                                        value={currentSelectedMilestoneId || undefined}
                                        onChange={(val) => handleMilestoneChange(val)}
                                        allowClear
                                        onClear={() => handleMilestoneChange(null)}
                                        className="flex-1 text-[13px]"
                                        variant="borderless"
                                        size="small"
                                        showSearch
                                        optionFilterProp="label"
                                        popupClassName="dark:bg-[#1e293b]"
                                        disabled={(projectMilestones || []).length === 0}
                                    >
                                        <Select.Option value={null} label="No Milestone (Unassigned)">
                                            <div className="flex items-center gap-2 text-slate-400 text-[12px]">
                                                <span className="material-symbols-outlined text-sm">block</span>
                                                <span>No Milestone (Unassigned)</span>
                                            </div>
                                        </Select.Option>
                                        {(projectMilestones || []).map((m) => (
                                            <Select.Option key={m._id} value={m._id} label={m.title || m.name}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-amber-500 font-bold text-xs">◆</span>
                                                    <span className="text-[13px] font-medium text-[#1c1e21] dark:text-white">
                                                        {m.title || m.name}
                                                    </span>
                                                    {m.status && (
                                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 ml-auto">
                                                            {m.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                        </>
                    )}
                    {(additionalProjects || []).map((project) => (
                        <ProjectRow
                            key={project._id || project}
                            project={project}
                            onRemove={() => handleRemoveAdditionalProject(project._id || project)}
                            editEstHours={editEstHours}
                            setEditEstHours={setEditEstHours}
                            editEstMinutes={editEstMinutes}
                            setEditEstMinutes={setEditEstMinutes}
                            editEffortHours={editEffortHours}
                            setEditEffortHours={setEditEffortHours}
                            editEffortMinutes={editEffortMinutes}
                            setEditEffortMinutes={setEditEffortMinutes}
                        />
                    ))}
                </div>
            </div>

            {/* Effort Bar rendered inside the card section at the very bottom */}
            {hasProjectSelected && !isMilestone && (
                <div className="pt-2 border-t border-[#dddfe2]">
                    <EffortBar
                        estimatedTime={task.estimatedTime}
                        actualTimeMs={displayActualTimeMs}
                        remainingTimeMs={remainingTimeMs}
                    />
                </div>
            )}
        </section>
    );
}

function ProjectRow({
    project,
    onRemove,
    editEstHours,
    setEditEstHours,
    editEstMinutes,
    setEditEstMinutes,
    editEffortHours,
    setEditEffortHours,
    editEffortMinutes,
    setEditEffortMinutes,
}) {
    const [expanded, setExpanded] = useState(true); // default expanded

    return (
        <div className="rounded-xl border border-[#dddfe2] dark:border-[#303030] bg-white dark:bg-[#1f1f1f] overflow-hidden">
            {/* Project header */}
            <div
                onClick={() => setExpanded(v => !v)}
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#f7f8fa] dark:hover:bg-[#262626] transition-colors"
            >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span
                        className="material-symbols-outlined text-[#65676b] transition-transform duration-200 shrink-0"
                        style={{ fontSize: '16px', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    >
                        chevron_right
                    </span>
                    <div
                        className="w-3.5 h-3.5 rounded-full shrink-0"
                        style={{ backgroundColor: project?.color || '#03488a', borderRadius: '4px' }}
                    />
                    <Tooltip title={project?.name || project?.title || 'Associated Project'} placement="top">
                        <span className="text-[13px] font-semibold text-[#1c1e21] dark:text-white truncate">
                            {project?.name || project?.title || 'Associated Project'}
                        </span>
                    </Tooltip>
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
                    className="text-[#65676b] hover:text-red-500 transition-colors shrink-0 ml-2"
                >
                    <span className="material-symbols-outlined text-base">close</span>
                </button>
            </div>

            {/* Expanded details table */}
            {expanded && (
                <div className="border-t border-[#e9ebee]">
                    <table className="w-full text-[13px] border-collapse">
                        <tbody>
                            <tr className="border-b border-[#e9ebee]">
                                <td className="px-4 py-2.5 text-[#65676b] w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base">check_circle</span>
                                        QA Testing
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-[#65676b] border-l border-[#e9ebee] w-1/2">—</td>
                            </tr>
                            <tr className="border-b border-[#e9ebee]">
                                <td className="px-4 py-2.5 text-[#65676b] w-1/2">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base">schedule</span>
                                        Maintenance
                                    </div>
                                </td>
                                <td className="px-4 py-2.5 text-[#65676b] border-l border-[#e9ebee] w-1/2">—</td>
                            </tr>
                            {/* Estimated Time Input */}
                            <tr className="border-b border-[#e9ebee] dark:border-[#303030]">
                                <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                    <div className="flex items-center gap-2 font-medium">
                                        <span className="text-[#65676b] dark:text-slate-400 font-bold text-base">#</span>
                                        Estimated Time
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="number" min={0}
                                            value={editEstHours || 0}
                                            onChange={(e) => setEditEstHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">h</span>
                                        <input
                                            type="number" min={0} max={59}
                                            value={editEstMinutes || 0}
                                            onChange={(e) => setEditEstMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">m</span>
                                    </div>
                                </td>
                            </tr>

                            {/* Effort Time Input */}
                            <tr>
                                <td className="px-4 py-2.5 text-[#65676b] dark:text-slate-400 w-1/2">
                                    <div className="flex items-center gap-2 font-medium">
                                        <span className="text-[#65676b] dark:text-slate-400 font-bold text-base">#</span>
                                        Effort Time
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-[#1c1e21] dark:text-white border-l border-[#e9ebee] dark:border-[#303030] w-1/2">
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="number" min={0}
                                            value={editEffortHours || 0}
                                            onChange={(e) => setEditEffortHours(Math.max(0, parseInt(e.target.value, 10) || 0))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">h</span>
                                        <input
                                            type="number" min={0} max={59}
                                            value={editEffortMinutes || 0}
                                            onChange={(e) => setEditEffortMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                                            className="w-14 bg-[#f0f2f5] dark:bg-[#141414] hover:bg-[#e4e6e9] dark:hover:bg-[#262626] focus:bg-white dark:focus:bg-[#141414] border border-transparent focus:border-[#dddfe2] dark:focus:border-[#303030] rounded px-1.5 py-0.5 text-[13px] font-semibold text-[#1c1e21] dark:text-white text-center outline-none transition-all"
                                        />
                                        <span className="text-[12px] text-[#65676b] dark:text-slate-400">m</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
